import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { AiEffort, AiProvider } from '@/types/investigation';
import type { CompanySla, SlaSettingsResponse, SlaSettingsRow } from '@/types/sla';

export type { AiEffort, AiProvider };

export interface AiModelChoice {
  provider: AiProvider;
  model: string;
  effort: AiEffort;
}
export interface SteppedAiModelChoice extends AiModelChoice {
  maxSteps: number;
}

export interface AiSettings {
  enabled: boolean;
  triage: AiModelChoice & { enabled: boolean };
  tiers: {
    reply: AiModelChoice;
    light: SteppedAiModelChoice;
    deep: SteppedAiModelChoice;
  };
  updatedBy?: string;
  updatedAt?: string;
}
// USD per million tokens
export interface AiModelPricing {
  input: number;
  output: number;
  cacheRead: number;
  cacheWrite: number;
}
export interface AiCatalogModel {
  provider: string;
  id: string;
  label: string;
  pricing?: AiModelPricing;
  // The efforts this model takes, ascending; absent means all of `efforts`
  efforts?: string[];
}
export interface AiSettingsResponse {
  settings: AiSettings;
  catalog: AiCatalogModel[];
  efforts: string[];
}

export const aiSettingsKeys = { all: ['ai-settings'] as const };

export function useAiSettings(enabled = true) {
  return useQuery({
    queryKey: aiSettingsKeys.all,
    queryFn: () => api<AiSettingsResponse>('/support-tickets/ai-settings'),
    enabled
  });
}

export function useSaveAiSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (settings: AiSettings) => api<{ settings: AiSettings }>('/support-tickets/ai-settings', { method: 'PUT', body: settings }),
    onSuccess: () => qc.invalidateQueries({ queryKey: aiSettingsKeys.all })
  });
}

// Design doc "Ticket references" (2026-08-24): per-company Linear-style ref
// prefixes, e.g. "CXG". Mirrors the ai-settings GET/PUT shape above —
// isCustom distinguishes an admin-set prefix (company.clientTracker.refPrefix)
// from a derived default, which the row still shows (effective, never blank).
export interface TrackerRefPrefixRow {
  companyId: string;
  name: string;
  refPrefix: string;
  isCustom: boolean;
}

export const trackerRefPrefixKeys = { all: ['tracker-ref-prefixes'] as const };

export function useTrackerRefPrefixes() {
  return useQuery({
    queryKey: trackerRefPrefixKeys.all,
    queryFn: () => api<{ data: TrackerRefPrefixRow[] }>('/support-tickets/tracker/ref-prefixes')
  });
}

// Changing a prefix only affects tickets minted after the change — the
// server never renumbers or re-prefixes an existing ref. Returns the
// updated row so the caller can optimistically reconcile without a refetch.
export function useSetTrackerRefPrefix() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { companyId: string; refPrefix: string }) =>
      api<TrackerRefPrefixRow>('/support-tickets/tracker/ref-prefixes', { method: 'PUT', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: trackerRefPrefixKeys.all })
  });
}

// The support team: who receives new-ticket and assignment emails and appears
// in the assignee picker. An empty selection means every Etainabl admin.
export interface StaffSettings {
  staffUserIds: string[];
  // userId -> Slack member ID, used to @-mention the assignee on #client-support posts.
  slackUserIds?: Record<string, string>;
  updatedBy?: string;
  updatedAt?: string;
}
export interface StaffAdmin {
  _id: string;
  name?: string;
  email?: string;
}
export interface StaffSettingsResponse {
  settings: StaffSettings;
  admins: StaffAdmin[];
}

export const staffSettingsKeys = { all: ['staff-settings'] as const };

export function useStaffSettings() {
  return useQuery({
    queryKey: staffSettingsKeys.all,
    queryFn: () => api<StaffSettingsResponse>('/support-tickets/staff-settings')
  });
}

export function useSaveStaffSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { staffUserIds: string[]; slackUserIds: Record<string, string> }) =>
      api<{ settings: StaffSettings }>('/support-tickets/staff-settings', { method: 'PUT', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: staffSettingsKeys.all });
      // The assignee pickers list only the team once one is chosen.
      qc.invalidateQueries({ queryKey: ['support-tickets', 'admins'] });
    }
  });
}

// Per-company SLAs: off for every company until staff switch them on here.
export const slaSettingsKeys = { all: ['sla-settings'] as const };

export function useSlaSettings() {
  return useQuery({
    queryKey: slaSettingsKeys.all,
    queryFn: () => api<SlaSettingsResponse>('/support-tickets/sla-settings')
  });
}

export function useSaveSlaSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ companyId, sla }: { companyId: string; sla: CompanySla }) =>
      api<SlaSettingsRow>(`/support-tickets/sla-settings/${companyId}`, { method: 'PUT', body: sla }),
    onSuccess: () => qc.invalidateQueries({ queryKey: slaSettingsKeys.all })
  });
}

// The primary-contact choices: the support team, or every admin when no team is set.
// `include` keeps a contact who has since left the team listed, so the select isn't blank.
export function useSupportAdmins(include?: string | null) {
  return useQuery({
    queryKey: ['support-tickets', 'admins', include ?? null],
    queryFn: () => api<{ data: StaffAdmin[] }>('/support-tickets/admins', { query: include ? { include } : undefined })
  });
}
